// frontend/client/src/components/BrandWizard.jsx
// FIXED: Enum values aligned to User.js schema. PostHog removed.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ChevronRight, ChevronLeft, Check, Sparkles, Zap, Heart, Coffee, Plane, Dumbbell, BookOpen, Gamepad2, Mic2, Palette, Baby, Music, Briefcase, PawPrint, Camera } from 'lucide-react';

// FIX: was 6 generic small-business-style categories (Fashion, Food, Tech,
// Travel, Fitness, Education) — read like an enterprise/SMB intake form,
// not something built for content creators. Expanded to cover the actual
// range of creator niches.
const INDUSTRIES = [
  { id: 'fashion',    name: 'Fashion & Beauty',    icon: Heart,      color: 'pink' },
  { id: 'lifestyle',  name: 'Lifestyle & Vlogging', icon: Camera,     color: 'purple' },
  { id: 'food',       name: 'Food & Beverage',     icon: Coffee,     color: 'orange' },
  { id: 'tech',       name: 'Technology',          icon: Zap,        color: 'blue' },
  { id: 'gaming',     name: 'Gaming',              icon: Gamepad2,   color: 'violet' },
  { id: 'comedy',     name: 'Comedy & Entertainment', icon: Mic2,    color: 'yellow' },
  { id: 'music',      name: 'Music',               icon: Music,      color: 'rose' },
  { id: 'art',        name: 'Art & Design',        icon: Palette,    color: 'fuchsia' },
  { id: 'travel',     name: 'Travel & Tourism',    icon: Plane,      color: 'sky' },
  { id: 'fitness',    name: 'Health & Fitness',    icon: Dumbbell,   color: 'green' },
  { id: 'family',     name: 'Parenting & Family',  icon: Baby,       color: 'amber' },
  { id: 'business',   name: 'Business & Finance',  icon: Briefcase,  color: 'slate' },
  { id: 'pets',       name: 'Pets & Animals',      icon: PawPrint,   color: 'lime' },
  { id: 'education',  name: 'Education',           icon: BookOpen,   color: 'indigo' },
];

// ✅ FIXED: Aligned to User.js schema enum:
// brandTone: ['professional', 'casual', 'witty', 'inspiring', 'educational', 'edgy', 'warm', 'luxury']
// Removed: 'funny' (not in schema) — replaced with 'witty'
const TONES = [
  { id: 'professional', name: 'Professional' },
  { id: 'casual',       name: 'Casual' },
  { id: 'witty',        name: 'Witty' },
  { id: 'inspiring',    name: 'Inspiring' },
  { id: 'educational',  name: 'Educational' },
  { id: 'edgy',         name: 'Edgy' },
  { id: 'warm',         name: 'Warm' },
  { id: 'luxury',       name: 'Luxury' },
];

// ✅ FIXED: Aligned to User.js schema enum:
// visualStyle: ['minimal', 'bold', 'earth', 'pastel', 'dark', 'gradient']
// Removed: 'elegant' and 'playful' (not in schema)
const VISUAL_STYLES = [
  { id: 'minimal',  name: 'Minimal',  desc: 'Clean lines, lots of white space' },
  { id: 'bold',     name: 'Bold',     desc: 'High contrast, strong typography' },
  { id: 'earth',    name: 'Earth',    desc: 'Warm, natural tones' },
  { id: 'pastel',   name: 'Pastel',   desc: 'Soft, light color palette' },
  { id: 'dark',     name: 'Dark',     desc: 'Sleek dark backgrounds' },
  { id: 'gradient', name: 'Gradient', desc: 'Vibrant color transitions' },
];

export default function BrandWizard() {
  const { updateUserProfile } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);

  const [formData, setFormData] = useState({
    username: '',
    nickname: '',
    brandName: '',
    industry: '',
    targetAudience: '',
    brandTone: [],
    contentPillars: [{ name: '', description: '' }],
    brandColors: { primary: '#4F46E5', secondary: '#8B5CF6', accent: '#3B82F6' },
    visualStyle: '',
  });
  const [loading, setLoading] = useState(false);

  const steps = [
    { number: 1, title: 'Brand Basics' },
    { number: 2, title: 'Your Voice' },
    { number: 3, title: 'Visual Style' }
  ];

  const handleNext = () => { if (currentStep < 3) setCurrentStep(currentStep + 1); };
  const handleBack = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        username: formData.username.trim(),
        displayName: formData.nickname.trim(),
        brandName: formData.brandName.trim(),
        industry: formData.industry,
        targetAudience: formData.targetAudience,
        brandTone: formData.brandTone,
        contentPillars: formData.contentPillars
          .filter(p => p.name.trim() !== '')
          .map(p => p.name.trim()),
        pillarDescriptions: formData.contentPillars
          .filter(p => p.name.trim() !== '')
          .reduce((acc, p) => {
            acc[p.name.trim()] = p.description;
            return acc;
          }, {}),
        visualStyle: formData.visualStyle,
        brandColors: [
          formData.brandColors.primary,
          formData.brandColors.secondary,
          formData.brandColors.accent
        ],
        onboardingCompleted: true,
      };

      const result = await updateUserProfile(payload);

      // ✅ Custom telemetry — replaces PostHog
      try {
        const { api } = await import('../services/apiClient');
        await api.post('/analytics/telemetry', {
          event: 'brand_wizard_completed',
          metadata: {
            industry: formData.industry,
            brand_tones: formData.brandTone.length,
            visual_style: formData.visualStyle
          }
        });
      } catch (_) {
        // telemetry failure should never block navigation
      }

      if (result.success) {
        navigate('/dashboard');
      } else {
        console.error('Failed to save brand data:', result.error);
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const updatePillar = (index, field, value) => {
    const newPillars = [...formData.contentPillars];
    newPillars[index][field] = value;
    setFormData(prev => ({ ...prev, contentPillars: newPillars }));
  };

  const addPillar = () => setFormData(prev => ({
    ...prev,
    contentPillars: [...prev.contentPillars, { name: '', description: '' }]
  }));

  const toggleTone = (toneId) => {
    setFormData(prev => {
      const currentTones = Array.isArray(prev.brandTone) ? prev.brandTone : [];
      if (currentTones.includes(toneId)) {
        return { ...prev, brandTone: currentTones.filter(t => t !== toneId) };
      } else {
        return { ...prev, brandTone: [...currentTones, toneId].slice(-3) };
      }
    });
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.username.trim() !== '' &&
               formData.brandName.trim() !== '' &&
               formData.industry !== '';
      case 2:
        return Array.isArray(formData.brandTone) && formData.brandTone.length > 0 &&
               formData.contentPillars.some(p => p.name.trim() !== '');
      case 3:
        return formData.visualStyle !== '';
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-main)' }}>
            Tell us about yourself
          </h2>
          <div>
            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
              Username *
            </label>
            <p style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 8 }}>This will be visible to others</p>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => updateFormData('username', e.target.value)}
              placeholder="@yourusername"
              style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border-light)', background: 'var(--bg-surface)', color: 'var(--text-main)', fontSize: 14, fontFamily: 'var(--font-body)', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
              Your Name (Optional)
            </label>
            <input
              type="text"
              value={formData.nickname}
              onChange={(e) => updateFormData('nickname', e.target.value)}
              placeholder="Your full name or nickname"
              style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border-light)', background: 'var(--bg-surface)', color: 'var(--text-main)', fontSize: 14, fontFamily: 'var(--font-body)', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
              Brand Name *
            </label>
            <input
              type="text"
              value={formData.brandName}
              onChange={(e) => updateFormData('brandName', e.target.value)}
              placeholder="Enter your brand name"
              style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border-light)', background: 'var(--bg-surface)', color: 'var(--text-main)', fontSize: 14, fontFamily: 'var(--font-body)', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>
              Industry *
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {INDUSTRIES.map(industry => {
                const Icon = industry.icon;
                const selected = formData.industry === industry.id;
                return (
                  <button
                    key={industry.id}
                    onClick={() => updateFormData('industry', industry.id)}
                    style={{
                      padding: '14px 10px', borderRadius: 12, cursor: 'pointer',
                      border: selected ? '2px solid var(--brand-primary)' : '2px solid var(--border-light)',
                      background: selected ? 'var(--brand-secondary)' : 'var(--bg-surface)',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                      transition: 'all 0.15s'
                    }}
                  >
                    <Icon size={20} style={{ color: selected ? 'var(--brand-primary)' : 'var(--text-muted)' }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: selected ? 'var(--brand-primary)' : 'var(--text-main)' }}>
                      {industry.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      );

      case 2: return (
        <div className="space-y-6">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--text-main)' }}>
            Define your voice
          </h2>
          <div>
            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>
              Brand Tone — Select up to 3 *
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {TONES.map(tone => {
                const selected = Array.isArray(formData.brandTone) && formData.brandTone.includes(tone.id);
                return (
                  <button
                    key={tone.id}
                    onClick={() => toggleTone(tone.id)}
                    style={{
                      padding: '10px 8px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                      border: selected ? '2px solid var(--brand-primary)' : '2px solid var(--border-light)',
                      background: selected ? 'var(--brand-secondary)' : 'var(--bg-surface)',
                      color: selected ? 'var(--brand-primary)' : 'var(--text-main)',
                      transition: 'all 0.15s'
                    }}
                  >
                    {tone.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
              Content Pillars *
            </label>
            <p style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 10 }}>What topics will you focus on?</p>
            {formData.contentPillars.map((pillar, index) => (
              <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  type="text"
                  placeholder={`Pillar ${index + 1} (e.g. "Tips")`}
                  value={pillar.name}
                  onChange={(e) => updatePillar(index, 'name', e.target.value)}
                  style={{ width: '35%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border-light)', background: 'var(--bg-surface)', color: 'var(--text-main)', fontSize: 13, fontFamily: 'var(--font-body)' }}
                />
                <input
                  type="text"
                  placeholder="Short description..."
                  value={pillar.description}
                  onChange={(e) => updatePillar(index, 'description', e.target.value)}
                  style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border-light)', background: 'var(--bg-surface)', color: 'var(--text-main)', fontSize: 13, fontFamily: 'var(--font-body)' }}
                />
              </div>
            ))}
            <button
              onClick={addPillar}
              style={{ fontSize: 12, color: 'var(--brand-primary)', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: 4 }}
            >
              + Add Pillar
            </button>
          </div>
        </div>
      );

      case 3: return (
        <div className="space-y-6">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--text-main)' }}>
            Choose your visual style
          </h2>
          <div>
            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>
              Visual Style *
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {VISUAL_STYLES.map(style => {
                const selected = formData.visualStyle === style.id;
                return (
                  <button
                    key={style.id}
                    onClick={() => updateFormData('visualStyle', style.id)}
                    style={{
                      padding: '14px 12px', borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                      border: selected ? '2px solid var(--brand-primary)' : '2px solid var(--border-light)',
                      background: selected ? 'var(--brand-secondary)' : 'var(--bg-surface)',
                      transition: 'all 0.15s'
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 700, display: 'block', color: selected ? 'var(--brand-primary)' : 'var(--text-main)', marginBottom: 3 }}>
                      {style.name}
                    </span>
                    <span style={{ fontSize: 11, color: selected ? 'var(--brand-primary)' : 'var(--text-muted)' }}>
                      {style.desc}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
              Brand Colors
            </label>
            <p style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 12 }}>Choose colors that represent your brand</p>
            <div style={{ display: 'flex', gap: 20 }}>
              {['primary', 'secondary', 'accent'].map(key => (
                <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <input
                    type="color"
                    value={formData.brandColors[key]}
                    onChange={e => updateFormData('brandColors', { ...formData.brandColors, [key]: e.target.value })}
                    style={{ width: 56, height: 56, borderRadius: 10, border: '1px solid var(--border-light)', cursor: 'pointer', padding: 2 }}
                  />
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'capitalize' }}>{key}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );

      default: return null;
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-canvas)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ maxWidth: 680, width: '100%' }}>

        {/* Progress Steps */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: currentStep >= step.number ? 'var(--brand-primary)' : 'var(--border-medium)',
                    color: currentStep >= step.number ? 'var(--brand-primary-contrast, white)' : 'var(--text-muted)', fontWeight: 700, fontSize: 14, transition: 'all 0.2s'
                  }}>
                    {currentStep > step.number ? <Check size={18} /> : step.number}
                  </div>
                  <p style={{ marginTop: 6, fontSize: 11, fontWeight: 700, color: currentStep >= step.number ? 'var(--brand-primary)' : 'var(--text-muted)' }}>
                    {step.title}
                  </p>
                </div>
                {index < steps.length - 1 && (
                  <div style={{ flex: 1, height: 3, margin: '0 8px', marginBottom: 20, borderRadius: 2, background: currentStep > index + 1 ? 'var(--brand-primary)' : 'var(--border-light)', transition: 'background 0.3s' }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content Card */}
        <div style={{ background: 'var(--bg-surface)', borderRadius: 20, boxShadow: 'var(--shadow-xl)', border: '1px solid var(--border-light)', padding: '32px 36px', minHeight: 400 }}>
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            style={{
              padding: '10px 20px', borderRadius: 10, border: '1px solid var(--border-light)',
              background: 'var(--bg-surface)', color: 'var(--text-main)', fontSize: 13, fontWeight: 700,
              cursor: currentStep === 1 ? 'not-allowed' : 'pointer', opacity: currentStep === 1 ? 0.4 : 1,
              display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-body)'
            }}
          >
            <ChevronLeft size={16} /> Back
          </button>

          {currentStep < 3 ? (
            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              style={{
                padding: '10px 24px', borderRadius: 10, border: 'none',
                background: isStepValid() ? 'var(--brand-primary)' : 'var(--border-medium)',
                color: 'white', fontSize: 13, fontWeight: 700,
                cursor: isStepValid() ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-body)',
                boxShadow: isStepValid() ? 'var(--shadow-brand)' : 'none'
              }}
            >
              Next <ChevronRight size={16} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading || !isStepValid()}
              style={{
                padding: '10px 24px', borderRadius: 10, border: 'none',
                background: loading || !isStepValid() ? 'var(--border-medium)' : 'var(--brand-primary)',
                color: 'white', fontSize: 13, fontWeight: 700,
                cursor: loading || !isStepValid() ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-body)',
                boxShadow: !loading && isStepValid() ? 'var(--shadow-brand)' : 'none'
              }}
            >
              {loading ? 'Setting up...' : 'Complete Setup'} <Sparkles size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}